/**
 * OIDC Authentication Provider — Per-Realm Identity App
 *
 * Selects the Auth0 SPA `client_id` from `oidc.realmClients[realm_id]` when configured,
 * otherwise uses legacy `oidc.clientId`. Does not send `connection` on /authorize so
 * SAML and HRD can apply. Uses dynamic redirectUri from window.location.origin.
 */

import React, { useEffect, useRef } from "react";
import { AuthProvider as OidcAuthProvider } from "react-oidc-context";
import {
  getConfigSync,
  resolveOidcClientId,
  usesPerRealmOidcClients,
} from "../config";
import { useRealm } from "../context/RealmContext";
import { AUTH_RETURN_TO_KEY } from "./returnTo";

interface AuthProviderProps {
  children: React.ReactNode;
}

let tokenCleanupRan = false;

function runInitialTokenCleanup(): void {
  if (tokenCleanupRan || typeof window === "undefined") return;
  tokenCleanupRan = true;

  try {
    const storageKeys = Object.keys(localStorage);
    let cleaned = false;

    storageKeys.forEach((key) => {
      if (key.startsWith("oidc.")) {
        try {
          const value = localStorage.getItem(key);
          if (value) {
            let token: string | null = null;

            try {
              const data = JSON.parse(value);
              token = data.access_token || data.id_token || null;
            } catch {
              token = value;
            }

            if (token && typeof token === "string") {
              const parts = token.split(".");
              if (parts.length !== 3 && parts.length !== 5) {
                console.warn(
                  "[Token Cleanup] Removing old opaque token from storage:",
                  key
                );
                localStorage.removeItem(key);
                cleaned = true;
              }
            }
          }
        } catch {
          console.warn(
            "[Token Cleanup] Removing unparseable OIDC storage key:",
            key
          );
          localStorage.removeItem(key);
          cleaned = true;
        }
      }
    });

    if (cleaned && import.meta.env.DEV) {
      console.log("[Token Cleanup] Old tokens removed on startup.");
    }
  } catch (error) {
    console.error("[Token Cleanup] Error checking token storage:", error);
  }
}

runInitialTokenCleanup();

const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const config = getConfigSync();
  const { realmId } = useRealm();
  const isCallbackProcessed = useRef(false);

  const resolved = resolveOidcClientId(config, realmId);

  if (!resolved.ok) {
    const perRealm = usesPerRealmOidcClients(config);
    console.error("[OIDC Config Error] Missing required configuration:", {
      reason: resolved.reason,
      realmId,
      perRealm,
    });
    const message =
      resolved.reason === "missing_realm_mapping"
        ? `No OIDC client is configured for realm "${realmId}". Add this realm to oidc.realmClients in config.json (per-realm Auth0 SPA client IDs).`
        : resolved.reason === "missing_authority"
          ? "OIDC authority is missing or empty. Please check your config.json file."
          : "OIDC clientId is missing or empty. Set oidc.clientId or oidc.realmClients in config.json.";
    return (
      <div style={{ padding: "20px", textAlign: "center" }}>
        <h2>Configuration Error</h2>
        <p>{message}</p>
      </div>
    );
  }

  const clientId = resolved.clientId;
  const authority = config.oidc.authority.trim();

  let scope = config.oidc.scope || "openid profile email";
  if (!scope.includes("offline_access")) {
    scope = `${scope} offline_access`;
  }

  const redirectUri =
    typeof window !== "undefined"
      ? window.location.origin
      : "http://localhost:3030";

  const oidcConfig = {
    authority,
    client_id: clientId,
    redirect_uri: redirectUri,
    silent_redirect_uri: redirectUri,
    scope,
    post_logout_redirect_uri: redirectUri,
    extraQueryParams: {
      ...(config.oidc.audience && { audience: config.oidc.audience }),
    },
    automaticSilentRenew: true,
    accessTokenExpiringNotificationTimeInSeconds: 60,
    onSigninCallback: (user: unknown) => {
      if (isCallbackProcessed.current) return;
      isCallbackProcessed.current = true;

      const returnTo =
        sessionStorage.getItem(AUTH_RETURN_TO_KEY) || "/identity";
      sessionStorage.removeItem(AUTH_RETURN_TO_KEY);

      if (typeof window !== "undefined" && import.meta.env.DEV && user) {
        const typedUser = user as {
          access_token?: string;
          refresh_token?: string;
          expires_at?: number;
        };
        console.log("[OIDC Signin Callback]", {
          hasToken: !!typedUser.access_token,
          hasRefreshToken: !!typedUser.refresh_token,
          expiresAt: typedUser.expires_at,
          returnTo,
          realm: realmId,
        });
      }

      window.history.replaceState(null, "", returnTo);
      window.dispatchEvent(new PopStateEvent("popstate", { state: null }));
    },
  };

  useEffect(() => {
    if (typeof window === "undefined") return;

    const searchParams = new URLSearchParams(window.location.search);
    const hasOidcParams =
      searchParams.has("code") ||
      searchParams.has("state") ||
      searchParams.has("error");

    if (hasOidcParams && isCallbackProcessed.current) {
      const cleanUrl = window.location.pathname;
      window.history.replaceState(null, "", cleanUrl);
    }
  }, []);

  return <OidcAuthProvider {...oidcConfig}>{children}</OidcAuthProvider>;
};

export default AuthProvider;
